<template>
  <div class="intake-form-response">
    <p class="description" v-if="intakeForm.description">{{ intakeForm.description }}</p>

      <!-- PI contact information -->
      <h5>P.I. Contact Information</h5>

      <div class="pi-name">
        <dynamic-field
          :inputDefinition="defaultResponse['pIFirstName']"
          @update-validity="updateDefaultValid($event, 'pIFirstName')"
          v-model="defaultResponse['pIFirstName'].value"
        ></dynamic-field>

        <dynamic-field
          :inputDefinition="defaultResponse['pILastName']"
          @update-validity="updateDefaultValid($event, 'pILastName')"
          v-model="defaultResponse['pILastName'].value"
        ></dynamic-field>
      </div>

      <div class="pi-contact">
        <dynamic-field
          :inputDefinition="defaultResponse['pIPhone']"
          @update-validity="updateDefaultValid($event, 'pIPhone')"
          v-model="defaultResponse['pIPhone'].value"
        ></dynamic-field>

        <dynamic-field
          :inputDefinition="defaultResponse['pIEmail']"
          @update-validity="updateDefaultValid($event, 'pIEmail')"
          v-model="defaultResponse['pIEmail'].value"
        ></dynamic-field>
      </div>

      <!-- Additional contact information -->
      <h5>Additional Contacts</h5>

      <div class="aditional-contacts">
        <div class="additional-contact" v-for="(contact, i) of additionalContacts" :key="i">
          <dynamic-field
            :inputDefinition="contact.name"
            @update-validity="contact.name.valid = $event"
            v-model="contact.name.value"
          ></dynamic-field>

          <dynamic-field
            :inputDefinition="contact.phone"
            @update-validity="contact.phone.valid = $event"
            v-model="contact.phone.value"
          ></dynamic-field>

          <dynamic-field
            :inputDefinition="contact.email"
            @update-validity="contact.email.valid = $event"
            v-model="contact.email.value"
          ></dynamic-field>

          <div class="remove-contact">
            <i class="fal fa-times-circle" @click=removeContact(i)></i>
          </div>
        </div>
        <a class="av-link secondary" @click="addContact()"><i class="fal fa-plus-circle"></i> Add Contact</a>
      </div>

      <!-- Project information -->
      <h5>Project Information</h5>
      <div class="proposal">

        <!-- <dynamic-field
          v-if="!isEditingCayuse"
          :key="defaultResponse['proposalNumberType'].label"
          :inputDefinition="defaultResponse['proposalNumberType']"
          @update-validity="updateDefaultValid($event, 'proposalNumberType')"
          v-model="defaultResponse['proposalNumberType'].value"
        ></dynamic-field> -->

        <label class="av-label" v-if="!isEditingCayuse">Proposal Record Type</label>
        <av-radio-group v-if="!isEditingCayuse" v-model="defaultResponse['proposalNumberType'].value" @update:modelValue="updateDefaultValid($event, 'proposalNumberType')">
          <av-radio-button value="QuickProposal">Quick Proposal (QP)</av-radio-button>
          <av-radio-button value="ProposalDevelopment">Proposal Development (PD)</av-radio-button>
        </av-radio-group>

        <dynamic-field
          :inputDefinition="defaultResponse['proposalNumber']"
          @update-validity="updateDefaultValid($event, 'proposalNumber')"
          v-model="defaultResponse['proposalNumber'].value"
        ></dynamic-field>
      </div>

      <dynamic-field
        v-for="inputDef of intakeForm.inputs"
        :key="inputDef.label"
        :inputDefinition="inputDef"
        v-model="intakeFormResponse[inputDef.id].value"
        @update-validity="updateValid($event, inputDef.id)"
      ></dynamic-field>
  </div>
</template>

<script lang="ts">
import { ProposalNumberType } from '../models/proposalType'
import { cloneDeep, isNumber } from 'lodash'
import { defineComponent, PropType } from 'vue'
import { AppointmentTypeDetail } from '../models/appointmentType'
import { Contact, ContactIntput } from '../models/contact'
import { DynamicInput, WorkingDynamicInput } from '../models/dynamicInput'
import FormattedIntakeFormResponse from '../models/formattedIntakeFormResponse'
import IntakeForm from '../models/intakeForm'
import IntakeFormResponse from '../models/intakeFormResponse'
import DynamicField from './DynamicField.vue'

export default defineComponent({
  name: 'IntakeForm',

  props: {
    /** The intake form definition */
    intakeForm: {
      type: Object,
      required: true
    },

    currentResponse: {
      type: Object as PropType<FormattedIntakeFormResponse> | null,
      required: false,
      default: null
    }
  },

  emits: ['form-updated'],

  data () {
    return {
      proposalNumberType: '' as unknown as ProposalNumberType,
      defaultResponse: {},
      intakeFormResponse: {},
      additionalContacts: [] as Array<ContactIntput>
    }
  },

  computed: {
    isEditingCayuse (): boolean {
      if (this.currentResponse) {
        const propType = (this.currentResponse.defaultResponses as any).proposalNumberType.value
        if (propType === 'Cayuse') {
          return true
        } else {
          return false
        }
      }
      return false
    }
  },

  watch: {
    appointmentType: {
      handler () {
        this.resetIntakeFormResponse()
      },
      deep: true,
      immediate: true
    },

    defaultResponse: {
      handler () {
        this.$emit('form-updated', this.formatResponse())
      },
      deep: true,
      immediate: true
    },

    intakeFormResponse: {
      handler () {
        this.$emit('form-updated', this.formatResponse())
      },
      deep: true,
      immediate: true
    },

    additionalContacts: {
      handler () {
        this.$emit('form-updated', this.formatResponse())
      },
      deep: true,
      immediate: true
    }
  },

  methods: {
    /**
     * Adds a contact to the form
     */
    addContact () {
      this.additionalContacts.push(
        {
          name: { type: 'text', label: 'Name', required: false, valid: false, value: '' },
          phone: { type: 'phone', label: 'Phone', required: false, valid: false, value: '' },
          email: { type: 'email', label: 'Email', required: false, valid: false, value: '' }
        }
      )
    },

    /**
     * Indicates if all form inputs are valid
     */
    isValid (): boolean {
      const responses = Object.values(this.intakeFormResponse)
      const defaultResponses = Object.values(this.defaultResponse)

      return responses.reduce((previous: boolean, current: any) => {
        return previous && current.valid
      }, true) && defaultResponses.reduce((previous: boolean, current: any) => {
        return previous && current.valid
      }, true) && this.additionalContacts.reduce((previous: boolean, current: any) => {
        return previous && current.name.valid && current.email.valid && current.phone.valid
      }, true)
    },

    /**
     * Formats the current response and emits an event
     */
    formatResponse (): FormattedIntakeFormResponse {
      const responses = cloneDeep(Object.values(this.intakeFormResponse)) as Array<any>
      responses.forEach(r => {
        if (r.type === 'checkbox group') {
          r.value = r.value.join(', ')
        }

        if (r.type === 'checkbox') {
          r.value = String(r.value)
        }
      })

      const contacts = [] as Array<Contact>
      this.additionalContacts.forEach((c: ContactIntput) => {
        if (c.name.value + c.email.value + c.phone.value === '') {
          return
        }

        const contact = {
          name: c.name.value,
          email: c.email.value,
          phone: c.phone.value
        }

        contacts.push(contact)
      })

      return {
        defaultResponses: this.defaultResponse,
        additionalContacts: contacts,
        intakeFormResponses: responses,
        isValid: this.isValid()
      }
    },

    /**
     * Removes the given contact
     * @param ind: The index of the contact to remove
     */
    removeContact (ind: number) {
      this.additionalContacts.splice(ind, 1)
    },

    /**
     * Resets the responses of the intake form
     */
    resetIntakeFormResponse () {
      if (this.currentResponse == null) {
        this.additionalContacts = []
        this.addContact()
      } else {
        this.additionalContacts = this.currentResponse.additionalContacts.map(ac => {
          return {
            name: { type: 'text', label: 'Name', required: false, valid: false, value: ac.name },
            phone: { type: 'phone', label: 'Phone', required: false, valid: false, value: ac.phone },
            email: { type: 'email', label: 'Email', required: false, valid: false, value: ac.email }
          }
        })
      }

      const defaultFormResponse = {}

      const defaultFields = [
        { id: 'pIFirstName', label: 'P.I. First Name', type: 'text' },
        { id: 'pILastName', label: 'P.I. Last Name', type: 'text' },
        { id: 'pIEmail', label: 'P.I. Email', type: 'email' },
        { id: 'pIPhone', label: 'P.I. Phone #', type: 'phone' },
        { id: 'proposalNumberType', label: 'Proposal Type', type: 'radio', options: ['QuickProposal', 'ProposalDevelopment'] },
        { id: 'proposalNumber', label: 'Proposal Record Number (This will be the PD or QP number found on the respective Streamlyne record)', type: this.isEditingCayuse ? 'cayuse' : 'proposalNumber' }
      ]

      defaultFields.forEach(gf => {
        defaultFormResponse[gf.id] = {
          id: gf.id,
          valid: false,
          value: this.currentResponse ? this.currentResponse.defaultResponses[gf.id].value : '',
          label: gf.label,
          required: true,
          type: gf.type
        }

        if (gf.type === 'radio') {
          defaultFormResponse[gf.id].options = gf.options
        }

        if (gf.id === 'proposalNumberType' && !this.isEditingCayuse) {
          if (this.currentResponse) {
            const ptCode: ProposalNumberType = (this.currentResponse.defaultResponses as any).proposalNumberType.value as ProposalNumberType
            if (ptCode === 'QuickProposal') {
              defaultFormResponse[gf.id].value = 'QuickProposal'
            } else {
              defaultFormResponse[gf.id].value = 'ProposalDevelopment'
            }
            defaultFormResponse[gf.id].valid = true
          }
        } else if (gf.id === 'proposalNumberType') {
          defaultFormResponse[gf.id].required = false
          defaultFormResponse[gf.id].valid = true
        }
      })

      this.defaultResponse = defaultFormResponse

      const intakeFormResponse = {}

      this.intakeForm.inputs.forEach((input: DynamicInput) => {
        intakeFormResponse[input.id] = {
          id: input.id,
          type: input.type,
          label: input.label,
          value: input.type === 'checkbox' ? false : '',
          required: input.required,
          valid: false
        }

        if (input.type === 'radio' && !this.currentResponse) {
          intakeFormResponse[input.id].value = input.options[0]
        }

        if (input.type === 'checkbox group' && !this.currentResponse) {
          intakeFormResponse[input.id].value = []
        }
      })

      if (this.currentResponse) {
        this.currentResponse.intakeFormResponses.forEach(res => {
          if (res.type === 'checkbox') {
            intakeFormResponse[res.id].value = res.value === 'true'
          } else {
            intakeFormResponse[res.id].value = res.value
          }
        })
      }

      this.intakeFormResponse = intakeFormResponse
    },

    /**
     * Update valid property on global intake form
     */
    updateDefaultValid (val, id) {
      this.defaultResponse[id].valid = val
    },

    /**
     * Update valid property on intake form
     */
    updateValid (val, id) {
      this.intakeFormResponse[id].valid = val
    }
  },

  components: {
    DynamicField
  }
})
</script>
